// Trainings database operations
import {
  collection,
  doc,
  setDoc,
  getDocs,
  query,
  where,
  orderBy,
  serverTimestamp,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../firebase';
import { usersDB } from './users';
import { schoolsDB } from './schools';
import {
  calculateMultiplier,
  calculateDrillShurikens,
  calculateSessionBonusShurikens,
  calculateLeague,
  DEFAULT_REWARD_CONFIG,
} from '../shurikens';
import { schoolDate, schoolDayBoundsUtc, computeStreakUpdate, DEFAULT_SCHOOL_TIMEZONE } from '../streak';

// Training doc ID - timestamp-based. Callers should generate it up front (before any await)
// so every save of the same session targets the same document.
export const newTrainingId = () => new Date().toISOString().replace(/[:.]/g, '-');

// Firestore write promises only settle once the server acknowledges them, so offline they never
// resolve. The write is already queued in the local (IndexedDB) cache by then, so don't block on it.
const settleWrite = (writePromise) => {
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    writePromise.catch((err) => console.error('Queued write failed:', err));
    return Promise.resolve();
  }
  return writePromise;
};

// Prefix stats keys for a dotted-path update on the user doc
const statsUpdate = (fields) =>
  Object.fromEntries(Object.entries(fields).map(([key, value]) => [`stats.${key}`, value]));

export const trainingsDB = {
  // Log training session
  logTraining: async (userId, trainingData, trainingId = newTrainingId()) => {
    // trainingData = {
    //   drills: [{ drillId, drillName, durationSeconds, notes }],
    //   totalDurationSeconds,
    //   startedAt,
    //   completedAt
    // }
    
    try {
      // 1. Read current state (served from the local cache when offline)
      const userData = await usersDB.getUserData(userId);
      const stats = userData.stats;
      const schoolDoc = await schoolsDB.getSchool(userData.profile.schoolId).catch(() => null);
      const rewardConfig = { ...DEFAULT_REWARD_CONFIG, ...(schoolDoc?.rewardConfig || {}) };
      const schoolTimezone = schoolDoc?.rewardConfig?.weekStart?.timezone || DEFAULT_SCHOOL_TIMEZONE;
      const today = schoolDate(new Date(), schoolTimezone);
      const lastDate = stats.lastTrainingDate;

      // 2. Calculate new streak
      const { newStreak, streakIncremented, streakBroken } = computeStreakUpdate(stats, today);

      // 3. Check if milestone reached
      const milestones = [7, 14, 30, 60, 100];
      const isFirstDayMilestone = streakIncremented && newStreak === 1 && !lastDate;
      const milestoneReached = streakIncremented && (milestones.includes(newStreak) || isFirstDayMilestone);

      // 4. Check if daily goal reached
      const todayTrainings = await trainingsDB.getTodayTrainings(userId, schoolTimezone);
      const totalSecondsToday = todayTrainings
        .filter(t => t.id !== trainingId)
        .reduce((sum, t) => sum + t.totalDurationSeconds, 0) + trainingData.totalDurationSeconds;
      const dailyGoalReached = totalSecondsToday >= (userData.settings.dailyGoalMinutes * 60);

      // 5. Write the training and the stats atomically, so a session that syncs later
      //    can never land without its streak update (shurikens are awarded separately via awardShurikens)
      const multiplier = calculateMultiplier(newStreak);
      const batch = writeBatch(db);
      batch.set(doc(db, 'users', userId, 'trainings', trainingId), {
        ...trainingData,
        createdAt: serverTimestamp()
      });
      batch.update(doc(db, 'users', userId), statsUpdate({
        currentStreak: newStreak,
        longestStreak: Math.max(newStreak, stats.longestStreak),
        totalTrainingSeconds: stats.totalTrainingSeconds + trainingData.totalDurationSeconds,
        totalSessions: stats.totalSessions + 1,
        lastTrainingDate: today,
        'shurikens.multiplier': multiplier,
      }));
      await settleWrite(batch.commit());

      return {
        success: true,
        trainingId,
        newStreak,
        streakIncremented,
        streakBroken,
        milestoneReached: milestoneReached ? newStreak : null,
        dailyGoalReached,
        multiplier,
        rewardConfig,
        isFirstEverDrill: !stats.hasCompletedFirstDrill,
      };
    } catch (error) {
      console.error('Error logging training:', error);
      throw error;
    }
  },

  // Get today's trainings
  getTodayTrainings: async (userId, schoolTimezone = DEFAULT_SCHOOL_TIMEZONE) => {
    try {
      // startedAt is a UTC ISO string, so query the school day's real UTC bounds
      const { start, end } = schoolDayBoundsUtc(new Date(), schoolTimezone);
      
      const trainingsRef = collection(db, 'users', userId, 'trainings');
      const q = query(
        trainingsRef,
        where('startedAt', '>=', start),
        where('startedAt', '<', end),
        orderBy('startedAt', 'desc')
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting today\'s trainings:', error);
      throw error;
    }
  },

  // Get trainings in date range
  getTrainings: async (userId, { startDate, endDate }) => {
    try {
      const trainingsRef = collection(db, 'users', userId, 'trainings');
      const q = query(
        trainingsRef,
        where('startedAt', '>=', startDate.toISOString()),
        where('startedAt', '<=', endDate.toISOString()),
        orderBy('startedAt', 'desc')
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting trainings:', error);
      throw error;
    }
  },

  // Update training session
  updateTraining: async (userId, trainingId, trainingData) => {
    // trainingData = {
    //   drills: [{ drillId, drillName, durationSeconds, notes }],
    //   totalDurationSeconds,
    //   sessionNotes
    // }
    
    try {
      const trainingRef = doc(db, 'users', userId, 'trainings', trainingId);
      
      // Calculate new total duration
      const totalDurationSeconds = trainingData.drills.reduce(
        (sum, drill) => sum + drill.durationSeconds, 
        0
      );
      
      // Update the training document
      await updateDoc(trainingRef, {
        drills: trainingData.drills,
        totalDurationSeconds,
        sessionNotes: trainingData.sessionNotes || null,
        updatedAt: serverTimestamp()
      });
      
      return {
        success: true,
        totalDurationSeconds
      };
    } catch (error) {
      console.error('Error updating training:', error);
      throw error;
    }
  },

  // Update training session and user's training date (for auto-save during active session)
  updateTrainingAndDate: async (userId, trainingId, trainingData) => {
    // trainingData = {
    //   drills: [{ drillId, drillName, durationSeconds, notes }],
    //   totalDurationSeconds,
    //   startedAt,
    //   sessionNotes
    // }
    
    try {
      const trainingRef = doc(db, 'users', userId, 'trainings', trainingId);
      
      // Calculate new total duration
      const totalDurationSeconds = trainingData.drills.reduce(
        (sum, drill) => sum + drill.durationSeconds, 
        0
      );
      
      const today = schoolDate(new Date());
      const userData = await usersDB.getUserData(userId);
      const stats = userData.stats;

      const batch = writeBatch(db);
      batch.update(trainingRef, {
        drills: trainingData.drills,
        totalDurationSeconds,
        sessionNotes: trainingData.sessionNotes || null,
        updatedAt: serverTimestamp(),
        // Update completedAt to current time to reflect the latest activity
        completedAt: new Date().toISOString()
      });

      // Update user's lastTrainingDate to today to maintain streak (session crossed midnight).
      // Same batch as the training update, so it can't get lost if the app closes before sync.
      let newStreak = null;
      if (stats.lastTrainingDate !== today) {
        ({ newStreak } = computeStreakUpdate(stats, today));
        batch.update(doc(db, 'users', userId), statsUpdate({
          currentStreak: newStreak,
          longestStreak: Math.max(newStreak, stats.longestStreak),
          lastTrainingDate: today
        }));
      }
      await settleWrite(batch.commit());

      return newStreak === null
        ? { success: true, totalDurationSeconds, streakUpdated: false }
        : { success: true, totalDurationSeconds, streakUpdated: true, newStreak };
    } catch (error) {
      console.error('Error updating training and date:', error);
      throw error;
    }
  },

  // Award shurikens for a completed session (call once at session end with full drill list)
  awardShurikens: async (userId, drills) => {
    try {
      const userData = await usersDB.getUserData(userId);
      const stats = userData.stats;
      const schoolDoc = await schoolsDB.getSchool(userData.profile.schoolId).catch(() => null);
      const rewardConfig = { ...DEFAULT_REWARD_CONFIG, ...(schoolDoc?.rewardConfig || {}) };

      const multiplier = stats.shurikens?.multiplier || calculateMultiplier(stats.currentStreak || 0);
      const isFirstEverDrill = !stats.hasCompletedFirstDrill;

      let sessionShurikens = 0;
      let prevSessionSeconds = 0;

      for (let i = 0; i < (drills || []).length; i++) {
        const drill = drills[i];
        const drillIsFirst = isFirstEverDrill && i === 0;
        const drillShurikens = calculateDrillShurikens(drill.durationSeconds, multiplier, drillIsFirst, rewardConfig);
        const newSessionSeconds = prevSessionSeconds + drill.durationSeconds;
        const bonusShurikens = calculateSessionBonusShurikens(prevSessionSeconds, newSessionSeconds, multiplier, rewardConfig);
        sessionShurikens += drillShurikens + bonusShurikens;
        prevSessionSeconds = newSessionSeconds;
      }

      const currentShurikens = stats.shurikens || { total: 0, weekly: 0 };
      const newTotalShurikens = (currentShurikens.total || 0) + sessionShurikens;
      const newWeeklyShurikens = (currentShurikens.weekly || 0) + sessionShurikens;
      const newLeague = calculateLeague(newWeeklyShurikens, stats.currentLeague || 'lead', rewardConfig);

      await usersDB._updateStats(userId, {
        'shurikens.total': newTotalShurikens,
        'shurikens.weekly': newWeeklyShurikens,
        currentLeague: newLeague,
        ...(isFirstEverDrill && { hasCompletedFirstDrill: true }),
      });

      // Keep leaderboard subcollection in sync so Leaderboard page shows current data
      const schoolId = userData.profile.schoolId;
      if (schoolId) {
        const leaderboardEntryRef = doc(db, 'schools', schoolId, 'leaderboard', userId);
        await setDoc(leaderboardEntryRef, {
          name: userData.profile.name || '?',
          weekly: newWeeklyShurikens,
          total: newTotalShurikens,
          league: newLeague,
          isWeeklyChampion: userData.stats?.badges?.weeklyChampion || false,
          lastKnownRank: userData.stats?.lastKnownRank ?? null,
        }, { merge: true }).catch(err => console.warn('Leaderboard sync failed:', err));
      }

      return {
        sessionShurikens,
        newTotalShurikens,
        newWeeklyShurikens,
        newLeague,
        multiplier,
      };
    } catch (error) {
      console.error('Error awarding shurikens:', error);
      throw error;
    }
  },
};

// Legacy exports for backward compatibility
export const createTraining = trainingsDB.logTraining;
export const getTrainings = trainingsDB.getTrainings;
export const getTraining = trainingsDB.getTodayTrainings;
